import 'firebase/auth';
import { firstValueFrom } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';

import { DialogService } from 'src/app/services/dialog.service';
import { UIMessagingService } from 'src/app/services/uimessaging.service';
import { environment } from 'src/environments/environment';
import { LogLevelsDictionary } from '../../dictionaries/LogLevels';
import { UserRoles } from '../../dictionaries/UserRoles';
import { LogService } from '../../services/log.service';
import { AuthService } from '../../services/auth.service';
import { FirebaseUtilitiesService } from '../../services/firebase-utilities.service';
import { take } from 'rxjs/operators';

import { FgpasswordComponent } from '../fgpassword/fgpassword.component';

@Component({
    selector: 'app-signin',
    templateUrl: './signin.component.html',
    styleUrls: ['./signin.component.scss'],
    standalone: false
})
export class SigninComponent implements OnInit {
  hide = true;
  twofacode: string;
  twofacode_needed: boolean;
  errorMessage: string;
  userSignedIn: boolean;
  hiddenClass: string;
  UserRoles: {
    owner: string;
    admin: string;
    associate: string;
    consultant: string;
    superuser: string;
  };
  dialogConfig: MatDialogConfig;
  dialogRef: any;
  codeValidated: Promise<true | Error>;
  emailFieldDisabled: boolean;
  forbidSignIn = false;
  isLoggingIn = false;
  password = new UntypedFormControl('', [Validators.required]);
  email = new UntypedFormControl('', [Validators.required, Validators.email]);

  constructor(
    private router: Router,
    private auth_$: AuthService,
    private route: ActivatedRoute,
    private dialog_$: DialogService,
    private firebase_$: FirebaseUtilitiesService,
    private uiMessaging_$: UIMessagingService,
    private log_$: LogService,
    private http: HttpClient
  ) {
    this.emailFieldDisabled = false;
    this.UserRoles = {
      owner: UserRoles.owner,
      admin: UserRoles.admin,
      associate: UserRoles.associate,
      consultant: UserRoles.consultant,
      superuser: UserRoles.superuser
    };
    this.userSignedIn = false;
    this.twofacode = '';
    this.twofacode_needed = false;
    this.errorMessage = '';
    window.document.body.focus();
  }

  ngOnInit() {
    this.email.valueChanges.subscribe(r => {
      if (r.trim() !== r) {
        this.email.setValue(r.trim(), { emitEvent: false });
        return;
      }
    });

    this.password.valueChanges.subscribe(r => {
      if (r.trim() !== r) {
        this.password.setValue(r.trim(), { emitEvent: false });
        return;
      }
    });

    // Pre-fill email from ?hint= param (e.g. when arriving via an image sharing email link)
    this.route.queryParams.pipe(take(1)).subscribe(params => {
      if (params['hint']) {
        this.email.setValue(decodeURIComponent(params['hint']));
        this.emailFieldDisabled = true;
      }
    });

    this.errorAuthSub();
  }

  backToHome() {
    this.router.navigateByUrl('/');
  }

  errorAuthSub() {
    this.auth_$.errorAuth.subscribe(error => {
      this.isLoggingIn = false;
      // Handle raw error messages from AuthService
      if (typeof error === 'string') {
        if (error.includes('INVALID_LOGIN_CREDENTIALS')) {
          this.errorMessage = 'Incorrect email or password';
        } else if (error.includes('auth/')) {
          // This is likely a Firebase error message, use our centralized handling
          this.handleLoginError({ code: error, message: error });
        } else {
          this.errorMessage = error;
        }
      } else {
        this.errorMessage = error;
      }
    });
  }

  getPrompt2faTitle() {
    return 'Two-factor authentication';
  }

  getPrompt2faMessage() {
    return 'Please enter the code sent to your phone or email.';
  }

  handleCancel(ev) {
    window.location.reload();
  }

  handleConfirm(ev) {
    if (!ev) {
      // Log detailed error for debugging
      this.log_$.storeLog(
        this.auth_$.userData.getValue() || { email: this.email.value },
        LogLevelsDictionary.error,
        { error: 'Invalid 2FA code', component: 'SigninComponent', method: 'handleConfirm' },
        '2FA Code Validation Error'
      );

      this.errorMessage = 'The verification code you entered is incorrect.';
      this.auth_$.errorAuth.next('The verification code you entered is incorrect. Please try again.');
      this.uiMessaging_$.toastMessage(
        'The verification code you entered is incorrect. Please check the code and try again.',
        'Error'
      );
    } else {
      console.log('Ready to signin');
      this.handleLogin().then(r => console.log(r));
    }
  }

  getErrorMessage(formControl) {
    if (formControl.hasError('required')) {
      return 'This field is required';
    }

    if (formControl.hasError('email')) {
      return 'Please enter a valid email address';
    }

    return '';
  }

  clearErrorMessage() {
    this.errorMessage = '';
    this.auth_$.errorAuth.next('');
  }

  private isNetworkError(error): boolean {
    return (
      error.code === 'auth/network-request-failed' ||
      error.message?.includes('network') ||
      error.message?.includes('timeout') ||
      !navigator.onLine
    );
  }

  private getNetworkErrorMessage(): string {
    if (!navigator.onLine) {
      return 'You appear to be offline. Please check your internet connection and try again.';
    }
    return 'Unable to connect to our servers. Please check your internet connection and try again.';
  }

  async login(event) {
    event.preventDefault();
    event.stopPropagation();
    this.hide = true;
    if (this.email.valid && this.password.valid) {
      await this.handleLogin();
      return;
    }
    return;
  }

  handleLoginError(error) {
    // Log detailed error for debugging
    this.log_$.storeLog(
      this.auth_$.userData.getValue() || { email: this.email.value },
      LogLevelsDictionary.error,
      { error: error, component: 'SigninComponent', method: 'handleLoginError' },
      'Login Error'
    );

    // Handle different error formats
    const code = error?.code || error?.error?.code;
    const message = error?.message || error?.error?.message;

    let userMessage: string;
    let actionSuggestion: string = '';

    // Check if this is a raw JSON error response
    if (typeof error === 'string' && error.includes('INVALID_LOGIN_CREDENTIALS')) {
      userMessage = 'Incorrect email or password';
      actionSuggestion = "Please check your credentials and try again, or reset your password if you've forgotten it.";
    } else {
      switch (code) {
        case 'auth/too-many-requests':
          userMessage = 'Too many failed login attempts. Your account has been temporarily locked for security.';
          actionSuggestion = 'Please wait 15 minutes before trying again, or reset your password.';
          break;
        case 'auth/wrong-password':
          userMessage = 'Incorrect password';
          actionSuggestion = "Please check your password and try again, or reset your password if you've forgotten it.";
          break;
        case 'auth/user-not-found':
          userMessage = 'Account not found';
          actionSuggestion =
            'Please check your email address or contact your administrator if you believe this is an error.';
          break;
        case 'auth/invalid-email':
          userMessage = 'Please enter a valid email address.';
          actionSuggestion = 'Make sure your email address is formatted correctly (e.g., user@example.com).';
          break;
        case 'auth/user-disabled':
          userMessage = 'This account has been disabled.';
          actionSuggestion = 'Please contact your administrator for assistance.';
          break;
        case 'auth/network-request-failed':
          userMessage = this.getNetworkErrorMessage();
          actionSuggestion = '';
          break;
        case 'auth/operation-not-allowed':
          userMessage = 'Email/password sign-in is not enabled for this account.';
          actionSuggestion = 'Please contact your administrator for assistance.';
          break;
        default:
          if (this.isNetworkError(error)) {
            userMessage = this.getNetworkErrorMessage();
            actionSuggestion = '';
          } else {
            userMessage = 'We encountered an unexpected error during sign-in.';
            actionSuggestion = 'Please try again in a few moments. If the problem persists, contact support.';
          }
          break;
      }
    }

    // Set error message for display (concise version for UI)
    this.errorMessage = userMessage;

    // Show toast with full message including action suggestion
    if (actionSuggestion) {
      this.uiMessaging_$.toastMessage(`${userMessage}. ${actionSuggestion}`, 'Error');
    } else {
      this.uiMessaging_$.toastMessage(userMessage, 'Error');
    }

    // Clear password field for security
    this.password.setValue('');
    this.password.markAsUntouched();
  }

  async handleLogin() {
    let user;

    try {
      const email = this.email.value.toLowerCase();

      // Check if the account is locked before attempting login
      try {
        const lockoutStatus = await this.checkAccountLockout(email);
        if (lockoutStatus.locked) {
          const minutes = Math.ceil(lockoutStatus.remainingSeconds / 60);
          this.errorMessage = `Account is temporarily locked. Please try again in ${minutes} minute(s).`;
          this.uiMessaging_$.toastMessage(
            `Too many failed login attempts. Your account is locked for ${minutes} minute(s).`,
            'Error'
          );
          this.password.setValue('');
          this.password.markAsUntouched();
          return;
        }
      } catch (lockoutError) {
        // If lockout check fails, allow login to proceed (fail-open for availability)
        console.error('Lockout check failed, proceeding with login:', lockoutError);
      }

      const existingRestrictions = await this.validateAccountRestrictions(email);
      if (!existingRestrictions) {
        return;
      }

      this.setEmailAndPasswordValues();
      const password = this.password.value;

      user = await this.auth_$.login(email, password);

      // auth_$.login() returns null on failure (error swallowed via .catch()), UserCredential on success
      if (!user) {
        // Record the failed login attempt
        try {
          const attemptResult = await this.recordLoginAttempt(email, false);
          if (attemptResult.locked) {
            const minutes = Math.ceil((attemptResult.remainingSeconds || 900) / 60);
            this.errorMessage = `Too many failed attempts. Account locked for ${minutes} minute(s).`;
            this.uiMessaging_$.toastMessage(
              `Your account has been locked after ${attemptResult.failedCount} failed attempts. Please try again in ${minutes} minute(s).`,
              'Error'
            );
            this.password.setValue('');
            this.password.markAsUntouched();
          } else if (attemptResult.attemptsRemaining !== undefined && attemptResult.attemptsRemaining > 0) {
            this.uiMessaging_$.toastMessage(
              `Incorrect credentials. ${attemptResult.attemptsRemaining} attempt(s) remaining before account lockout.`,
              'Error'
            );
          }
        } catch (recordError) {
          console.error('Failed to record login attempt:', recordError);
        }
        return;
      }

      // Login succeeded — reset the lockout counter
      this.recordLoginAttempt(email, true).catch(err =>
        console.error('Failed to reset login attempt counter:', err)
      );

      if (this.auth_$.redirectURL) {
        this.router.navigateByUrl(this.auth_$.redirectURL);
      } else {
        return this.runUserReady(user);
      }
    } catch (error) {
      // Log detailed error for debugging
      this.log_$.storeLog(
        this.auth_$.userData.getValue() || { email: this.email.value },
        LogLevelsDictionary.error,
        { error: error, component: 'SigninComponent', method: 'handleLogin' },
        'Login Process Error'
      );

      this.handleLoginError(error);
      throw error;
    }
  }

  runUserReady(user) {
    try {
      this.auth_$.userReady(user.user, 'runUserReady 2').then(r => {
        this.log_$.storeLog(this.auth_$.userData.getValue(), LogLevelsDictionary.info, {}, 'Login');
        this.auth_$.checkOwnerPlan();
      });
      return 200;
    } catch (error) {
      return this.handleUserReadyError(error);
    }
  }

  async handle2FAData(email = this.email.value) {
    try {
      const res = await this.handleLogin();
      if (!res || res.a === null) {
        return;
      }
      // await this.handleTwoFactorSignIn(email);
    } catch (error) {
      // Log detailed error for debugging
      this.log_$.storeLog(
        this.auth_$.userData.getValue() || { email: email },
        LogLevelsDictionary.error,
        { error: error, component: 'SigninComponent', method: 'handle2FAData' },
        '2FA Data Error'
      );

      // Use the centralized error handling
      this.handleLoginError(error);
      return;
    }
  }

  handleChange() {
    this.forbidSignIn = false;
    this.clearErrorMessage();
  }

  navigateToFGPass() {
    this.dialog_$.open(FgpasswordComponent, { width: '350px', data: { email: this.email.value } });
  }

  async signIn() {
    this.forbidSignIn = true;
    this.isLoggingIn = true;
    try {
      await this.handle2FAData();
      this.route.queryParams.subscribe(params => {
        if (params['referer']) {
          const cloneObject = { ...params };
          delete cloneObject['referer'];
          const url = this.router.createUrlTree([params['referer']], { queryParams: cloneObject });
          this.router.navigateByUrl(url, { replaceUrl: true });
        }
      });
    } finally {
      this.forbidSignIn = false;
      this.isLoggingIn = false;
    }
  }

  async validateCode(code) {
    return await this.firebase_$.validate2FACode(this.email.value, code, 'email');
  }

  setEmailAndPasswordValues() {
    this.email.setValue(this.email.value.trim().toLowerCase());
    this.password.setValue(this.password.value.trim());
  }

  handleUserReadyError(error) {
    // Log detailed error for debugging
    this.log_$.storeLog(
      this.auth_$.userData.getValue() || { email: this.email.value },
      LogLevelsDictionary.error,
      { error: error, component: 'SigninComponent', method: 'handleUserReadyError' },
      'User Ready Error'
    );

    let userMessage: string;

    switch (error.code) {
      case 'auth/wrong-password':
        userMessage = 'The password you entered is incorrect. Please try again or reset your password.';
        break;
      case 'auth/user-not-found':
        userMessage =
          'No account found with this email address. Please check your email or contact your administrator.';
        break;
      case 'auth/user-disabled':
        userMessage = 'This account has been disabled. Please contact your administrator for assistance.';
        break;
      case 'auth/network-request-failed':
        userMessage = 'Unable to connect to our servers. Please check your internet connection and try again.';
        break;
      default:
        userMessage = 'We encountered an unexpected error. Please try again in a few moments.';
        break;
    }

    this.auth_$.errorAuth.next(userMessage);
    this.twofacode_needed = false;
    return error;
  }

  private async validateAccountRestrictions(email): Promise<boolean> {
    try {
      const url = `${environment.constants.cloudfunctionsURL}auth-preSignIn`;
      const result = await firstValueFrom(this.http.post<{ allowed: boolean; reason?: string }>(url, { email }));
      if (!result.allowed) {
        this.errorMessage = 'Your account access is currently restricted. Please contact your administrator.';
        this.uiMessaging_$.toastMessage(
          'Your account access is currently restricted. Please contact your administrator for assistance.',
          'Error'
        );
        return false;
      }
      return true;
    } catch (error) {
      this.log_$.storeLog(
        this.auth_$.userData.getValue() || { email: email },
        LogLevelsDictionary.error,
        { error: error, component: 'SigninComponent', method: 'validateAccountRestrictions' },
        'Account Restrictions Validation Error'
      );

      this.errorMessage = 'Unable to verify account permissions. Please contact your administrator.';
      this.uiMessaging_$.toastMessage(
        'Unable to verify account permissions. Please contact your administrator for assistance.',
        'Error'
      );

      return false;
    }
  }

  private checkAccountLockout(email: string): Promise<{ locked: boolean; remainingSeconds?: number }> {
    const url = `${environment.constants.cloudfunctionsURL}auth-checkAccountLockout`;
    return firstValueFrom(this.http.post<{ locked: boolean; remainingSeconds?: number }>(url, { email }));
  }

  private recordLoginAttempt(email: string, success: boolean): Promise<any> {
    const url = `${environment.constants.cloudfunctionsURL}auth-recordLoginAttempt`;
    return firstValueFrom(this.http.post<any>(url, { email, success }));
  }
}
